import { userJoinFarmWithCodeAsync } from "@/base/farmhq-api"
import { useFormValidation } from "@/hooks/useFormvalidation"
import { useRequestHelpers } from "@/hooks/useRequestHelpers"
import { getUserFarmErrorCodeFromState } from "@/redux/selectors"
import { useRootSelector } from "@/redux/useRootSelector"
import {
  AppText,
  Box,
  CancelButton,
  FormControl,
  FormError,
  SubmitButton,
} from "@/theme"
import { useRouter } from "expo-router"
import React from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

/**
 * Form for joining a farm with a code.
 */
export function JoinFarmForm({
  onCancel,
  showInstructions,
}: {
  onCancel: (() => void) | null
  showInstructions: boolean
}): React.JSX.Element {
  const { t } = useTranslation("noFarms")
  const router = useRouter()
  const form = useForm({ defaultValues: { codeString: "" } })
  const { required } = useFormValidation()
  const errorCode = useRootSelector(getUserFarmErrorCodeFromState)
  const { dispatch, handleError, isLoading, toasts } = useRequestHelpers(
    "UserJoinFarmWithCode",
  )

  const onSubmit = form.handleSubmit(({ codeString }) => {
    dispatch(userJoinFarmWithCodeAsync({ codeString }))
      .unwrap()
      .then((response) => {
        if (response.codeStatus === "SUCCESS") {
          toasts.success()
          router.dismissAll()
          router.navigate("/")
          if (onCancel) {
            onCancel()
          }
        }
        return response
      })
      .catch((error) =>
        handleError(error, {
          toastMessage: "default",
        }),
      )
  })
  const validationError =
    errorCode === "INVALID"
      ? t("codeInvalid", { ns: "validation" })
      : errorCode === "EXPIRED"
        ? t("codeExpired", { ns: "validation" })
        : errorCode === "ALREADY_JOINED"
          ? t("joinFarmAlreadyJoined", { ns: "noFarms" })
          : undefined
  return (
    <React.Fragment>
      {showInstructions ? (
        <Box mb={"$2"}>
          <AppText>{t("joinFarmDescription")}</AppText>
        </Box>
      ) : null}
      <Controller
        control={form.control}
        name={"codeString"}
        rules={{ required }}
        render={({ field: { onChange, ref, ...field }, fieldState }) => {
          const errorMessage = fieldState.error?.message
          const isInvalid = Boolean(errorMessage)
          return (
            <FormControl.Provider id={"codeString"} isInvalid={isInvalid}>
              <FormControl.Label>{t("farmJoinCodeLabel")}</FormControl.Label>
              <FormControl.Input
                ref={ref}
                autoCapitalize={"characters"}
                autoCorrect={false}
                placeholder={t("codeStringFieldPlaceholder", { ns: "auth" })}
                textContentType={"oneTimeCode"}
                onChangeText={(nextValue) =>
                  onChange(nextValue.toLocaleUpperCase())
                }
                {...field}
              />
              <FormControl.ErrorMessage>
                {errorMessage}
              </FormControl.ErrorMessage>
            </FormControl.Provider>
          )
        }}
      />

      <Box mt={"$2"}>
        <FormError>{validationError}</FormError>
      </Box>
      <Box mt={"$4"}>
        <SubmitButton isLoading={isLoading} onPress={onSubmit} />
        {onCancel ? (
          <Box mt={"$4"}>
            <CancelButton
              isDisabled={isLoading}
              variant={"text"}
              onPress={onCancel}
            />
          </Box>
        ) : null}
      </Box>
    </React.Fragment>
  )
}
